import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Package, Beaker, Sparkles, Coffee, BadgeCheck, GlobeLock } from 'lucide-react';
import TiltCard from './TiltCard';
import ContactBanner from './ContactBanner';
import FlipCard from './FlipCard';

export default function TypesPage({onPageChange} : {onPageChange: (page: string) => void}) {
  const sugarTypes = [
    {
      name: "Bulk Raw Sugar",
      description: "Unrefined sugar primarily intended for further processing and refining. Contains natural molasses and has a golden brown color.",
      icon: <Package className="w-6 h-6 text-amber-800" />,
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
      image: "https://sandringhambulkfoods.co.nz/cdn/shop/files/F324AA1A-5053-43B6-BF98-6C385BEB693E.jpg?v=1716789535",
      uses: ["Industrial refining", "Food manufacturing", "Export markets"],
      characteristics: ["Polarization 96-99.5°", "Natural molasses content", "Lower processing cost"]
    },
    {
      name: "White Refined Sugar",
      description: "Fully refined sugar with high purity, crystal white appearance, and neutral taste. The most common form for direct consumption.",
      icon: <Sparkles className="w-6 h-6 text-gray-800" />,
      bgColor: "bg-gray-300",
      textColor: "text-gray-800",
      image: "https://www.fugohk.com/upload5/uploads/large/csf115.png",
      uses: ["Household consumption", "Bakery industry", "Beverage production"],
      characteristics: ["99.9% sucrose purity", "Crystal white color", "Neutral taste profile"]
    },
    {
      name: "Direct Consumption Sugar",
      description: "Minimally processed sugar retaining some natural molasses, offering enhanced flavor and nutritional content.",
      icon: <Coffee className="w-6 h-6 text-lime-800" />,
      bgColor: "bg-lime-300",
      textColor: "text-teal-800",
      image: "https://images.unsplash.com/photo-1641679103706-fc8542e2a97a?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      uses: ["Premium retail markets", "Organic food industry", "Artisanal products"],
      characteristics: ["Natural molasses flavor", "Golden to brown color", "Higher mineral content"]
    },
    {
      name: "Specialty Sugars",
      description: "Value-added sugar products including organic, muscovado, and other premium varieties for niche markets.",
      icon: <Beaker className="w-6 h-6 text-blue-700" />,
      bgColor: "bg-blue-300",
      textColor: "text-blue-700",
      image: "https://assets.epicurious.com/photos/63e139132d16855fd03c2be3/master/pass/MakePowderedSugar_HERO_020223_46601.jpg",
      uses: ["Gourmet cooking", "Health-conscious consumers", "Export premium markets"],
      characteristics: ["Unique flavor profiles", "Specialty processing", "Premium pricing"]
    }
  ];

    const productionProcesses = [
    {
      num: 1,
      label: 'Harvest',
      color: 'bg-lime-300',
      content: 'Harvesting involves gathering mature crops from the fields, ensuring optimal yield and quality for processing or sale.',
      img: 'https://images.unsplash.com/photo-1641347072423-7d8447466a1e?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    },
    {
      num: 2,
      label: 'Extraction',
      color: 'bg-yellow-400',
      content: 'Extraction involves crushing the harvested sugarcane to release juice, which is then filtered to remove impurities for further processing.',
      img: 'https://imbibemagazine.com/wp-content/uploads/2016/08/novo-fogo-feeding-press.jpg'
      },
    {
      num: 3,
      label: 'Processing',
      color: 'bg-violet-400',
      content: 'Processing involves refining the extracted sugar, removing impurities, and converting it into the desired product form.',
      img: 'https://www.bma-worldwide.com/fileadmin/Templates/BMA/photos/header-images/BAG_Ausr%C3%BCstungen_Diffuseur_Header.jpg'
    },
    {
      num: 4,
      label: 'Packaging',
      color: 'bg-blue-400',
      content: 'Packaging involves wrapping or containerizing the processed sugar for distribution, storage, or transportation.',
      img: 'https://www.allpackchina.com/wp-content/uploads/2024/01/Flat-Bottom-Pouch.jpg'
    },
  ]

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Types of Sugar</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            ACP and LDC countries produce a diverse range of sugar products, from bulk raw sugar 
            for refining to premium specialty varieties for direct consumption.
          </p>
        </div>


        {/* Sugar Types Grid */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-8">
            {sugarTypes.map((sugar, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`${sugar.bgColor} p-2 rounded-full`}>
                      {sugar.icon}
                    </div>
                    <CardTitle className='font-semibold'>{sugar.name}</CardTitle>
                  </div>
                  <ImageWithFallback
                    src={sugar.image}
                    alt={sugar.name}
                    className="rounded-lg w-full h-48 object-cover"
                  />
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 text-base mb-4">
                    {sugar.description}
                  </CardDescription>
                  
                  <div className="space-y-4 flex justify-between px-5 gap-4">
                    <div className='w-[50%]'>
                      <h4 className="font-semibold text-green-900 mb-2 bg-green-100 py-1 rounded-xl pl-5">Primary Uses</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {sugar.uses.map((use, i) => (
                          <li key={i}>• {use}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className='w-[50%]'>
                      <h4 className="font-semibold text-amber-900 mb-2 bg-amber-100 py-1 rounded-xl pl-5">Key Characteristics</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {sugar.characteristics.map((char, i) => (
                          <li key={i}>• {char}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Production Process */}
        <section className="mb-16 py-10">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-20">Sugar Production Process</h2>
          <div className='flex flex-wrap justify-between align-center'>
            {productionProcesses.map((card, index) => (
              <FlipCard key={index} item={card} />
            ))}
          </div>
        </section>

        {/* Quality Standards */}
        <section className="mb-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-50">Quality Standards & Specifications</h2>
          <div className="flex relative">
            <TiltCard className='w-[50%]'>
              <div className='bg-red-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 group-hover:shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">International Standards</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• ISO 5339 - Raw cane sugar specifications</li>
                  <li>• Codex Alimentarius standards for white sugar</li>
                  <li>• EU regulations for organic sugar</li>
                  <li>• Fairtrade certification requirements</li>
                  <li>• Bonsucro sustainability standards</li>
                </ul>
              </div>
              <GlobeLock className="scale-600 text-red-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
            </TiltCard>
            <TiltCard className='w-[50%] top-[-50%] absolute right-10 z-5'>
              <div className='bg-green-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quality Parameters</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Polarization (sucrose content)</li>
                  <li>• Moisture content</li>
                  <li>• Ash content and conductivity</li>
                  <li>• Color and optical properties</li>
                  <li>• Granulation and crystal size</li>
                </ul>
                <BadgeCheck className="scale-600 text-green-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
              </div>
            </TiltCard>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full flex items-center">
          <ContactBanner onPageChange={onPageChange} />
        </section>

        {/* Innovation and Trends */}
        {/* <section className="bg-amber-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Innovation & Market Trends</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Emerging Products</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Functional sugars with added nutrients</li>
                <li>• Low-glycemic index sugar varieties</li>
                <li>• Artisanal and heritage cane varieties</li>
                <li>• Carbon-neutral certified sugars</li>
                <li>• Blockchain-tracked provenance sugars</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Market Trends</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Growing demand for sustainable production</li>
                <li>• Premium market segment expansion</li>
                <li>• Health and wellness positioning</li>
                <li>• Transparency in supply chains</li>
                <li>• Direct-trade relationships</li>
              </ul>
            </div>
          </div>
        </section> */}
      </div>
    </div>
  );
}